import React, { useState } from "react";
import { UTILS } from "utils";
import { API } from "api";
import PropTypes from "prop-types";
import StoreAccessRightsList from "./StoreAccessRightsList";
import { CONSTANTS as C } from "config";
import { useTranslation } from "react-i18next";
import AppSimpleTable from "components/ui/AppSimpleTable";
import AppHeading2 from "components/ui/AppHeading2";
import AppIcon from "components/ui/AppIcon";
import AppModal from "components/ui/AppModal";
import AppButton from "components/ui/AppButton";

const getRowAdminId = (row) =>
  row?._id ?? row?.id ?? row?.userId ?? row?.adminId;

const getCurrentUserAdminId = (current) =>
  current?.userId ?? current?._id ?? current?.ID;

const ManageAdminUsers = (props) => {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState({});
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    user: null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { t } = useTranslation();

  const deleteUser = (e, index, row) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const adminId = getRowAdminId(row);
    if (!adminId) {
      UTILS.HANDLE_ERROR_NOTIFICATION(
        "topRight",
        "error",
        t("Could not determine user id for this row.")
      );
      return;
    }
    if (row.userType === C.USER_TYPE_ADMIN) {
      UTILS.HANDLE_ERROR_NOTIFICATION(
        "topRight",
        "info",
        t("BRAND-ADMIN rights can not be changed")
      );
      return;
    }
    const current = UTILS.getCurrentUser();
    if (String(adminId) === String(getCurrentUserAdminId(current))) {
      UTILS.HANDLE_ERROR_NOTIFICATION(
        "topRight",
        "info",
        t("You cannot delete your own account")
      );
      return;
    }
    setDeleteModal({ isOpen: true, user: row });
  };

  const handleCloseDeleteModal = () =>
    setDeleteModal({ isOpen: false, user: null });

  const confirmDeleteUser = async () => {
    const target = deleteModal.user;
    const adminId = getRowAdminId(target);
    if (!adminId) {
      handleCloseDeleteModal();
      return;
    }
    const userDetail = UTILS.getCurrentUser();
    const brandId = userDetail?.brandId?._id ?? userDetail?.brandId;
    if (!userDetail?.token || !brandId) {
      UTILS.HANDLE_ERROR_NOTIFICATION(
        "topRight",
        "error",
        t("Your session is missing token or brand. Please sign in again.")
      );
      return;
    }
    setDeleteLoading(true);
    try {
      const result = await API.DELETE_BRAND_MANAGER_USER({
        token: userDetail.token,
        brandId,
        adminId: String(adminId),
      });
      if (result?.success == 1) {
        UTILS.successNotification(
          result?.message || t("User removed successfully")
        );
        handleCloseDeleteModal();
        props.onUsersChange?.();
      } else {
        UTILS.errorNotification(
          result?.message || t("Something went wrong. Please try again.")
        );
      }
    } catch (e) {
      UTILS.errorNotification(
        e?.message || t("Something went wrong. Please try again.")
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const editUser = (index, userRow) => {
    const id = getRowAdminId(userRow);
    setUser(id ? { ...userRow, _id: id } : userRow);
    if (userRow.userType === C.USER_TYPE_ADMIN)
      UTILS.HANDLE_ERROR_NOTIFICATION(
        "topRight",
        "info",
        t("BRAND-ADMIN rights can not be changed")
      );
    else setOpen(true);
  };

  const handleCloseModal = () => setOpen(false);

  const columns = [
    {
      title: "User type",
      key: "userType",
      dataIndex: "userType",
    },
    {
      title: "Email",
      key: "email",
      dataIndex: "email",
    },
    {
      title: "Mobile number",
      key: "phoneNumber",
      dataIndex: "phoneNumber",
    },
    {
      title: "Actions",
      key: "actions",
      dataIndex: "actions",
      width: "120px",
      render: (_, row, index) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              editUser(index, row);
            }}
            aria-label={t("Edit")}
          >
            <AppIcon name="edit" />
          </button>
          <button
            type="button"
            className="inline-flex cursor-pointer"
            onClick={(e) => deleteUser(e, index, row)}
            aria-label={t("Delete")}
          >
            <AppIcon name="trash" />
          </button>
        </div>
      ),
    },
  ];

  const editFooter = (
    <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
      <AppButton variant="outline" size="lg" onClick={handleCloseModal}>
        {t("Close")}
      </AppButton>
    </div>
  );

  const deleteFooter = (
    <div className="mx-auto flex w-full max-w-[760px] items-center justify-center gap-4 pb-2">
      <AppButton
        variant="secondary"
        size="lg"
        className="w-1/2 border border-gray-400 bg-white text-4xl text-gray-500"
        disabled={deleteLoading}
        onClick={handleCloseDeleteModal}
      >
        {t("No, Keep It")}
      </AppButton>
      <AppButton
        variant="primary"
        size="lg"
        className="w-1/2 text-4xl"
        loading={deleteLoading}
        disabled={deleteLoading}
        onClick={confirmDeleteUser}
      >
        {t("Yes, Delete")}
      </AppButton>
    </div>
  );

  return (
    <div className="lg:px-5 py-4">
      <AppHeading2>{t("Edit Access Levels for Users")}</AppHeading2>
      <div>
        <AppModal
          open={open}
          onClose={handleCloseModal}
          width={720}
          destroyOnClose
          footer={editFooter}
          bodyStyle={{
            maxHeight: "calc(100vh - 220px)",
            overflowY: "auto",
            paddingTop: 8,
          }}
        >
          <StoreAccessRightsList user={user} />
        </AppModal>

        <AppModal
          open={deleteModal.isOpen}
          onClose={handleCloseDeleteModal}
          width={550}
          closable={false}
          destroyOnClose
          footer={deleteFooter}
        >
          <div className="flex flex-col items-center px-8 text-center">
            <div className="mb-6 mt-6 h-44 w-44">
              <AppIcon name="trashRed" size={170} className="h-full w-full" />
            </div>
            <h1 className="text-24px font-semibold text-gray-900">
              {t("Delete User")}
            </h1>
            <p className="mb-0 text-3xl text-gray-500">
              {deleteModal.user?.email
                ? t("Are you sure you want to delete {{name}}?", {
                    name: deleteModal.user.email,
                  })
                : t("Are you sure you want to delete this user?")}
            </p>
          </div>
        </AppModal>

        <AppSimpleTable columns={columns} data={props.users && props.users} />
      </div>
    </div>
  );
};

ManageAdminUsers.propTypes = {
  users: PropTypes.array,
  onUsersChange: PropTypes.func,
};

export default ManageAdminUsers;
